/**
 * Approximate server clock for countdowns (quote expiry, checkout reservation). Every API response
 * carries an HTTP `Date` header; the offset between it and the local clock is kept here so a
 * visitor whose computer clock is wrong still sees an honest countdown. Countdowns are display
 * only: when one reaches zero the UI asks the server for the real state (docs/04 section 6 —
 * "use server/database time").
 */

let offsetMs = 0;
let samples = 0;

/** Record the server's `Date` header (1 s resolution) observed at local time `localNow`. */
export function observeServerDate(dateHeader: string | null | undefined, localNow: number = Date.now()): void {
  if (!dateHeader) return;
  const server = Date.parse(dateHeader);
  if (!Number.isFinite(server)) return;
  // The header is truncated to the second: on average the real server time is ~500 ms later.
  const sample = server + 500 - localNow;
  // Smooth a little so one slow response does not make the countdown jump.
  offsetMs = samples === 0 ? sample : Math.round(offsetMs * 0.7 + sample * 0.3);
  samples = Math.min(samples + 1, 1000);
}

/** Milliseconds to add to the local clock to approximate the server's clock. */
export function serverOffsetMs(): number {
  return offsetMs;
}

/** Approximate server "now" in epoch milliseconds. */
export function serverNow(): number {
  return Date.now() + offsetMs;
}

/** Remaining milliseconds until an ISO deadline by the server's clock (0 when passed / invalid). */
export function msUntil(iso: string | null | undefined, now: number = serverNow()): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, t - now);
}

/** "9:41" / "0:07" countdown text. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Test hook: reset the learned offset. */
export function resetServerClock(): void {
  offsetMs = 0;
  samples = 0;
}
